import { Box, Button, TextField } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { type FormEvent, useState } from "react";

interface LoginFormProps {
  email: string;
  setEmail: (email: string) => void;
  loading: boolean;
  onSubmit: () => Promise<void>;
  onSwitchToPassword: () => void;
  verifyingEmail?: boolean;
  disabled?: boolean;
}

/**
 * Login form with magic link
 */
export function LoginForm({
  email,
  setEmail,
  loading,
  onSubmit,
  onSwitchToPassword,
  verifyingEmail = false,
  disabled = false,
}: LoginFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formLabel = verifyingEmail
    ? "Please enter your email id again to verify login link"
    : "Enter your email to get started";

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Box
      component="form"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <Text bold variant="body1" sx={{ textAlign: "center" }}>
        {formLabel}
      </Text>

      <TextField
        fullWidth
        required
        type="email"
        label="Email"
        placeholder="your.email@example.com"
        value={email}
        disabled={disabled || loading}
        autoComplete="email"
        sx={{
          "& .MuiOutlinedInput-root": {
            fontSize: "1rem",
            borderRadius: (theme) => theme.borderRadius?.xLarge,
            backgroundColor: "background.standout",
          },
        }}
        onChange={(event) => {
          setEmail(event.target.value);
        }}
      />

      <LoadingButton
        fullWidth
        type="submit"
        variant="primary"
        size="large"
        isLoading={isSubmitting || loading}
        disabled={disabled}
      >
        {verifyingEmail ? "Verify Email" : "Get Sign-in Link"}
      </LoadingButton>

      {/* Divider */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
        <Box sx={{ flex: 1, height: "1px", backgroundColor: "border.base" }} />
        <Text variant="body2" sx={{ color: "base.inactive" }}>
          or
        </Text>
        <Box sx={{ flex: 1, height: "1px", backgroundColor: "border.base" }} />
      </Box>

      {/* Alternative login option */}
      <Button
        variant="text"
        disabled={disabled || loading}
        sx={{ textTransform: "none", fontSize: "0.875rem", color: "primary.main" }}
        onClick={onSwitchToPassword}
      >
        Sign in with email & password
      </Button>
    </Box>
  );
}
